import { createRouter, createWebHistory } from 'vue-router'
import { setupRouterGuards } from './guards.js'

// Import route modules
import adminRoutes from './modules/admin.js'
import authRoutes from './modules/auth.js'
import storeRoutes from './modules/store.js'
import userRoutes from './modules/user.js'
import checkoutRoutes from './modules/checkout.js'
import brand, { filterRoutesByFeature } from '@/brand'

// Back/forward and reload restore the saved position, but the pages load their data after
// mounting, so at first the page is still short: the browser clamps the position to the footer
// and its scroll anchoring keeps the footer in view while the content fills in above it.
// Wait until the page is tall enough; stay at the top if it doesn't get there or the user scrolls first.
const SCROLL_CANCEL_EVENTS = ['wheel', 'touchstart', 'keydown']

const restoreScroll = (position, timeout = 3000) => new Promise((resolve) => {
  const started = performance.now()
  let cancelled = false
  const cancel = () => { cancelled = true }
  SCROLL_CANCEL_EVENTS.forEach((type) => window.addEventListener(type, cancel, { passive: true }))

  const check = () => {
    const reachable = document.documentElement.scrollHeight - window.innerHeight >= position.top
    if (!cancelled && !reachable && performance.now() - started < timeout) {
      requestAnimationFrame(check)
      return
    }
    SCROLL_CANCEL_EVENTS.forEach((type) => window.removeEventListener(type, cancel))
    resolve(reachable && !cancelled ? position : false)
  }
  check()
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  // Routes of features the brand has disabled are dropped (-> 404)
  routes: filterRoutesByFeature([
    // Store routes (public)
    ...storeRoutes,
    
    // Authentication routes
    ...authRoutes,
    
    // User dashboard routes (authenticated)
    ...userRoutes,
    
    // Checkout routes (public)
    ...checkoutRoutes,
    
    // Admin routes (admin only)
    ...adminRoutes,
    
    // Maintenance page
    {
      path: '/maintenance',
      name: 'maintenance',
      component: () => import('../views/MaintenanceView.vue'),
      meta: {
        title: `Údržba - ${brand.siteName}`
      }
    },

    // Catch-all route for 404
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      meta: {
        title: `Stránka nenájdená - ${brand.siteName}`
      }
    }
  ]),
  // Scroll behavior
  scrollBehavior(to, from, savedPosition) {
    if (to.meta.keepScroll && to.name === from.name) {
      return false
    } else if (savedPosition) {
      return restoreScroll(savedPosition)
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    } else {
      return { top: 0 }
    }
  }
})

// Setup router guards
setupRouterGuards(router)

router.beforeEach((to, from, next) => {
  next()
})

export default router
